/**
 * HR ACHIEVEMENT GRID
 *
 * Displays all HR achievements in a grid layout
 * Shows locked/unlocked states, progress, and categories
 * Professional design for recruiter gamification
 */

import React, { useState } from 'react';
import { Award, Lock, CheckCircle, Filter } from 'lucide-react';
import { HR_ACHIEVEMENTS, HRAchievement } from '../../constants/hrGamification';

interface UserAchievement {
  achievementId: string;
  unlockedAt: Date;
  progress?: number;
}

interface HRAchievementGridProps {
  userAchievements: UserAchievement[];
  onAchievementClick?: (achievement: HRAchievement) => void;
  showFilters?: boolean;
  compact?: boolean;
  className?: string;
}

const HRAchievementGrid: React.FC<HRAchievementGridProps> = ({
  userAchievements,
  onAchievementClick,
  showFilters = true,
  compact = false,
  className = ''
}) => {
  const [selectedCategory, setSelectedCategory] = useState<string>('all');
  const [selectedTier, setSelectedTier] = useState<string>('all');

  // Check if achievement is unlocked
  const isUnlocked = (achievementId: string): boolean => {
    return userAchievements.some(ua => ua.achievementId === achievementId);
  };

  // Get achievement progress if available
  const getProgress = (achievementId: string): number => {
    const userAchievement = userAchievements.find(ua => ua.achievementId === achievementId);
    return userAchievement?.progress || 0;
  };

  // Filter achievements by category and tier
  const filteredAchievements = HR_ACHIEVEMENTS.filter(achievement => {
    const categoryMatch = selectedCategory === 'all' || achievement.category === selectedCategory;
    const tierMatch = selectedTier === 'all' || achievement.tier === selectedTier;
    return categoryMatch && tierMatch;
  });

  // Count unlocked achievements
  const unlockedCount = HR_ACHIEVEMENTS.filter(a => isUnlocked(a.id)).length;
  const totalCount = HR_ACHIEVEMENTS.length;

  // Get tier badge color
  const getTierColor = (tier: HRAchievement['tier']): string => {
    switch (tier) {
      case 'bronze':   return 'bg-amber-100 text-amber-800 border-amber-300';
      case 'silver':   return 'bg-gray-100 text-gray-800 border-gray-300';
      case 'gold':     return 'bg-yellow-100 text-yellow-800 border-yellow-300';
      case 'platinum': return 'bg-purple-100 text-purple-800 border-purple-300';
      default:         return 'bg-gray-100 text-gray-800';
    }
  };

  // Get category icon
  const getCategoryIcon = (category: HRAchievement['category']): string => {
    switch (category) {
      case 'posting':     return '📋';
      case 'testing':     return '📝';
      case 'hiring':      return '🤝';
      case 'engagement':  return '🔥';
      case 'milestone':   return '🎯';
      case 'quality':     return '⭐';
      default:            return '🏆';
    }
  };

  return (
    <div className={`bg-white rounded-xl shadow-sm border border-gray-200 p-6 ${className}`}>
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center space-x-3">
          <Award className="w-6 h-6 text-yellow-600" />
          <h3 className="text-xl font-bold text-gray-900">Achievements</h3>
        </div>
        <div className="flex items-center space-x-2">
          <span className="text-sm font-semibold text-gray-600">
            {unlockedCount} / {totalCount}
          </span>
          <div className="w-24 h-2 bg-gray-200 rounded-full overflow-hidden">
            <div
              className="h-full bg-gradient-to-r from-yellow-400 to-orange-500"
              style={{ width: `${(unlockedCount / totalCount) * 100}%` }}
            />
          </div>
        </div>
      </div>

      {/* Filters */}
      {showFilters && (
        <div className="mb-6 space-y-3">
          {/* Category Filter */}
          <div>
            <div className="flex items-center space-x-2 mb-2">
              <Filter className="w-4 h-4 text-gray-500" />
              <span className="text-sm font-semibold text-gray-700">Category</span>
            </div>
            <div className="flex flex-wrap gap-2">
              <button
                onClick={() => setSelectedCategory('all')}
                className={`px-3 py-1.5 text-xs font-medium rounded-full transition-colors ${
                  selectedCategory === 'all'
                    ? 'bg-blue-600 text-white'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                All
              </button>
              <button
                onClick={() => setSelectedCategory('posting')}
                className={`px-3 py-1.5 text-xs font-medium rounded-full transition-colors ${
                  selectedCategory === 'posting'
                    ? 'bg-blue-600 text-white'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                📋 Posting
              </button>
              <button
                onClick={() => setSelectedCategory('testing')}
                className={`px-3 py-1.5 text-xs font-medium rounded-full transition-colors ${
                  selectedCategory === 'testing'
                    ? 'bg-blue-600 text-white'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                📝 Testing
              </button>
              <button
                onClick={() => setSelectedCategory('hiring')}
                className={`px-3 py-1.5 text-xs font-medium rounded-full transition-colors ${
                  selectedCategory === 'hiring'
                    ? 'bg-blue-600 text-white'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                🤝 Hiring
              </button>
              <button
                onClick={() => setSelectedCategory('engagement')}
                className={`px-3 py-1.5 text-xs font-medium rounded-full transition-colors ${
                  selectedCategory === 'engagement'
                    ? 'bg-blue-600 text-white'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                🔥 Engagement
              </button>
              <button
                onClick={() => setSelectedCategory('quality')}
                className={`px-3 py-1.5 text-xs font-medium rounded-full transition-colors ${
                  selectedCategory === 'quality'
                    ? 'bg-blue-600 text-white'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                ⭐ Quality
              </button>
            </div>
          </div>

          {/* Tier Filter */}
          <div>
            <span className="text-sm font-semibold text-gray-700 mb-2 block">Tier</span>
            <div className="flex flex-wrap gap-2">
              <button
                onClick={() => setSelectedTier('all')}
                className={`px-3 py-1.5 text-xs font-medium rounded-full transition-colors ${
                  selectedTier === 'all'
                    ? 'bg-blue-600 text-white'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                All Tiers
              </button>
              <button
                onClick={() => setSelectedTier('bronze')}
                className={`px-3 py-1.5 text-xs font-medium rounded-full transition-colors ${
                  selectedTier === 'bronze'
                    ? 'bg-amber-600 text-white'
                    : 'bg-amber-100 text-amber-800 hover:bg-amber-200'
                }`}
              >
                🥉 Bronze
              </button>
              <button
                onClick={() => setSelectedTier('silver')}
                className={`px-3 py-1.5 text-xs font-medium rounded-full transition-colors ${
                  selectedTier === 'silver'
                    ? 'bg-gray-600 text-white'
                    : 'bg-gray-100 text-gray-800 hover:bg-gray-200'
                }`}
              >
                🥈 Silver
              </button>
              <button
                onClick={() => setSelectedTier('gold')}
                className={`px-3 py-1.5 text-xs font-medium rounded-full transition-colors ${
                  selectedTier === 'gold'
                    ? 'bg-yellow-600 text-white'
                    : 'bg-yellow-100 text-yellow-800 hover:bg-yellow-200'
                }`}
              >
                🥇 Gold
              </button>
              <button
                onClick={() => setSelectedTier('platinum')}
                className={`px-3 py-1.5 text-xs font-medium rounded-full transition-colors ${
                  selectedTier === 'platinum'
                    ? 'bg-purple-600 text-white'
                    : 'bg-purple-100 text-purple-800 hover:bg-purple-200'
                }`}
              >
                💎 Platinum
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Achievement Grid */}
      <div className={`grid ${compact ? 'grid-cols-3' : 'grid-cols-2 md:grid-cols-3 lg:grid-cols-4'} gap-4`}>
        {filteredAchievements.map((achievement) => {
          const unlocked = isUnlocked(achievement.id);
          const progress = getProgress(achievement.id);

          return (
            <div
              key={achievement.id}
              onClick={() => onAchievementClick && onAchievementClick(achievement)}
              className={`relative group cursor-pointer rounded-lg border-2 p-4 transition-all duration-300 ${
                unlocked
                  ? 'bg-gradient-to-br from-yellow-50 to-orange-50 border-yellow-300 hover:shadow-lg hover:scale-105'
                  : 'bg-gray-50 border-gray-200 hover:bg-gray-100'
              }`}
            >
              {/* Tier Badge */}
              <div className={`absolute top-2 right-2 px-2 py-0.5 text-xs font-bold rounded-full border ${getTierColor(achievement.tier)}`}>
                {achievement.tier.toUpperCase()}
              </div>

              {/* Achievement Icon */}
              <div className="text-center mb-3">
                <div className={`text-5xl mb-2 transition-all ${unlocked ? 'grayscale-0' : 'grayscale opacity-40'}`}>
                  {achievement.emoji}
                </div>
                {unlocked && (
                  <CheckCircle className="w-5 h-5 text-green-600 mx-auto" />
                )}
                {!unlocked && (
                  <Lock className="w-5 h-5 text-gray-400 mx-auto" />
                )}
              </div>

              {/* Achievement Info */}
              <div className="text-center">
                <h4 className={`font-bold text-sm mb-1 ${unlocked ? 'text-gray-900' : 'text-gray-500'}`}>
                  {achievement.title}
                </h4>
                <p className={`text-xs mb-2 line-clamp-2 ${unlocked ? 'text-gray-600' : 'text-gray-400'}`}>
                  {achievement.description}
                </p>

                {/* Points */}
                <div className={`inline-flex items-center space-x-1 px-2 py-1 rounded-full text-xs font-semibold ${
                  unlocked
                    ? 'bg-yellow-100 text-yellow-800'
                    : 'bg-gray-100 text-gray-500'
                }`}>
                  <Award className="w-3 h-3" />
                  <span>+{achievement.points} XP</span>
                </div>

                {/* Progress Bar (for locked achievements) */}
                {!unlocked && progress > 0 && (
                  <div className="mt-3">
                    <div className="text-xs text-gray-600 mb-1">
                      {progress} / {achievement.requirement}
                    </div>
                    <div className="w-full h-1.5 bg-gray-200 rounded-full overflow-hidden">
                      <div
                        className="h-full bg-gradient-to-r from-blue-400 to-purple-500"
                        style={{ width: `${(progress / achievement.requirement) * 100}%` }}
                      />
                    </div>
                  </div>
                )}
              </div>

              {/* Category Icon (bottom left) */}
              <div className="absolute bottom-2 left-2 text-lg opacity-50">
                {getCategoryIcon(achievement.category)}
              </div>

              {/* Hover Tooltip */}
              <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 px-3 py-2 bg-gray-900 text-white text-xs rounded-lg opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none whitespace-nowrap z-10">
                {unlocked ? 'Unlocked!' : `${achievement.requirement} required`}
                <div className="absolute top-full left-1/2 transform -translate-x-1/2 border-4 border-transparent border-t-gray-900"></div>
              </div>
            </div>
          );
        })}
      </div>

      {/* Empty State */}
      {filteredAchievements.length === 0 && (
        <div className="text-center py-12">
          <Award className="w-16 h-16 text-gray-300 mx-auto mb-4" />
          <p className="text-gray-500 font-medium">No achievements in this category</p>
          <p className="text-sm text-gray-400 mt-1">Try selecting a different filter</p>
        </div>
      )}

      {/* Stats Summary (if not compact) */}
      {!compact && (
        <div className="mt-6 pt-6 border-t border-gray-200">
          <div className="grid grid-cols-4 gap-4 text-center">
            <div>
              <div className="text-2xl font-bold text-amber-600">
                {HR_ACHIEVEMENTS.filter(a => isUnlocked(a.id) && a.tier === 'bronze').length}
              </div>
              <div className="text-xs text-gray-600">Bronze</div>
            </div>
            <div>
              <div className="text-2xl font-bold text-gray-600">
                {HR_ACHIEVEMENTS.filter(a => isUnlocked(a.id) && a.tier === 'silver').length}
              </div>
              <div className="text-xs text-gray-600">Silver</div>
            </div>
            <div>
              <div className="text-2xl font-bold text-yellow-600">
                {HR_ACHIEVEMENTS.filter(a => isUnlocked(a.id) && a.tier === 'gold').length}
              </div>
              <div className="text-xs text-gray-600">Gold</div>
            </div>
            <div>
              <div className="text-2xl font-bold text-purple-600">
                {HR_ACHIEVEMENTS.filter(a => isUnlocked(a.id) && a.tier === 'platinum').length}
              </div>
              <div className="text-xs text-gray-600">Platinum</div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default HRAchievementGrid;
